// Offline thumbnails (BL-2429, ADR 066 — Offline Mode § 2): which media of a synced lot get a
// ~256px thumbnail on the device, the same-origin resizer URL, and the per-auction pass that
// keeps an auction's thumbnails matched to its stored lots, resumably (BUG-2124). The transfer
// itself is the device store's (BL-2609), so in the app the bytes never pass through the web view.
// Full-size files are never fetched.
import { RESIZED_IMAGE_WIDTH, resizedImageUrl } from '@auctogon/core/inventory';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import type { OfflineStore, OfflineThumbnailDownloadOutcome } from '../../gateways/native-device';

/**
 * Thumbnails fetched at once (BL-2627). On cache misses 4 at a time measured about 600 a minute
 * (BL-2579), so 8 reaches the edge's per-IP ceiling of 1,200 a minute and a cold pass is expected
 * to trip it. A 429 is waited out below, so the ceiling slows the pass rather than failing it.
 */
const FETCH_CONCURRENCY = 8;
/**
 * Thumbnails fetched and stored per batch; a switch-off is honoured between batches. A multiple of
 * {@link FETCH_CONCURRENCY}, so every slot stays busy until a batch's last wave.
 */
const FETCH_BATCH = 24;
/** Lot records read per batch while listing an auction's thumbnails, so a full pass stays small. */
const LOT_READ_BATCH = 200;
/**
 * The 4xx answers that can succeed on a retry (timeout, too early). A 429 is its own case, waited
 * out within the pass. Every other 4xx (a URL outside the resizer's pattern, a missing object) is a
 * refusal.
 */
const RETRYABLE_4XX = new Set([408, 425]);
/** The edge's throttle window (1,200 per 60s per IP, ADR 041 — Cloud Armor § 3): a 429 waits it out. */
const RATE_LIMIT_WAIT_MS = 60_000;
/** How often a wait checks whether the pass should stop instead. */
const WAIT_STEP_MS = 1_000;
/** Asks of one thumbnail the edge may throttle before it counts as a failure, retried next sync. */
const RATE_LIMITED_ATTEMPTS = 3;

/**
 * The resizer refused the image for good (a 4xx: a URL outside its one remote pattern, or a
 * missing object). Reported once, never retried until the lot changes.
 */
export class ThumbnailRefusedError extends Error {
  constructor(readonly status: number) {
    super(`thumbnail resize refused with ${status}`);
    this.name = 'ThumbnailRefusedError';
  }
}

/**
 * The edge throttled the resizer (a 429 from the per-IP `/_next/image` ceiling, ADR 041 — Cloud
 * Armor § 3). Not a failure: the pass waits out the window and asks again (BUG-2124).
 */
export class ThumbnailRateLimitedError extends Error {
  constructor() {
    super('thumbnail resize rate limited (429)');
    this.name = 'ThumbnailRateLimitedError';
  }
}

/** One thumbnail a lot should have on the device. */
export interface OfflineThumbnailSource {
  key: string;
  lotId: string;
  /** The image the thumbnail is made from: a photo's url, or a video's poster. */
  src: string;
}

/**
 * A thumbnail's store key. Per lot as well as per media item, because a copied or relisted lot
 * can carry the same media ids as the lot it came from.
 *
 * @param input - the lot id and the media id.
 * @returns the key.
 */
export function offlineThumbnailKey(input: { lotId: string; mediaId: string }): string {
  return `${input.lotId}:${input.mediaId}`;
}

/**
 * @param key - a key from {@link offlineThumbnailKey}.
 * @returns the lot id it belongs to.
 */
function lotIdOfKey(key: string): string {
  return key.slice(0, key.indexOf(':'));
}

/**
 * The thumbnails a lot should have: one per photo, from its url, and one per video that has a
 * poster, from the poster. A video's own file is never a source (BL-2429 AC 4, RAC 1).
 *
 * @param lot - the lot as the delta delivered it.
 * @returns its thumbnail sources, in media order.
 */
export function thumbnailSources(lot: OfflineDeltaLotDto): OfflineThumbnailSource[] {
  const lotId = lot.detail.id;
  return (lot.detail.media ?? []).flatMap((item): OfflineThumbnailSource[] => {
    const src = item.type === 'photo' ? item.url : item.thumbnail;
    return src == null || src === '' ? [] : [{ key: offlineThumbnailKey({ lotId, mediaId: item.id }), lotId, src }];
  });
}

/**
 * The thumbnail's source URL: Next's built-in resizer on the admin origin, so the request is
 * same-origin and only the small image crosses the network.
 *
 * @param src - the image's own URL.
 * @returns the resizer URL, the list thumbnails' own (BL-2442), so one cache entry serves both.
 */
export function thumbnailUrl(src: string): string {
  return resizedImageUrl(src, { width: RESIZED_IMAGE_WIDTH.listThumbnail });
}

/** What a failed download means for the pass. */
type FailureKind = 'throttled' | 'refused' | 'transient';

/**
 * @param status - the failed download's HTTP status, or null when there was no answer.
 * @returns how the pass treats it: a 429 is waited out, any other 4xx but a timeout is a refusal
 *   reported once, and everything else is retried on a later sync.
 */
export function classifyThumbnailFailure(status: number | null): FailureKind {
  if (status === 429) return 'throttled';
  if (status != null && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status)) return 'refused';
  return 'transient';
}

/** The error a failed outcome is reported with, typed by what it means. */
function failureError(outcome: Extract<OfflineThumbnailDownloadOutcome, { stored: false }>): Error {
  const kind = classifyThumbnailFailure(outcome.status);
  if (kind === 'refused' && outcome.status != null) return new ThumbnailRefusedError(outcome.status);
  if (kind === 'throttled') return new ThumbnailRateLimitedError();
  return outcome.error;
}

/** An auction's photo count while its photos download (BUG-2124). */
export interface ThumbnailProgress {
  /** Thumbnails the auction has on the device. */
  saved: number;
  /** Thumbnails it should have: the ones saved plus the ones the pass still has to download. */
  total: number;
  /** Bytes the pass has stored so far, for the auction's running size. */
  bytesStored: number;
  /** The auction's thumbnails on the device now: those it had plus those stored (BL-2609). */
  thumbnails: { count: number; bytes: number };
}

/** What one auction's thumbnail pass did. */
export interface AuctionThumbnailOutcome {
  /**
   * Lots whose thumbnails are still owed, written to the auction record so the next pass resumes
   * there: the lots a stopped pass did not reach, and lots with a failure worth retrying.
   */
  owedLotIds: string[];
  /** True when the pass stopped (`shouldStop`) before it had asked for every missing thumbnail. */
  stopped: boolean;
  /** How many thumbnails failed, refused ones included. */
  failed: number;
  /** The first failure's error, for the log's cause chain. */
  firstError: Error | null;
  /** Bytes the pass stored. */
  bytesStored: number;
}

/** What the pass needs. */
export interface AuctionThumbnailPassInput {
  /** The scope's device store; every thumbnail read and write goes through `store.thumbnails`. */
  store: OfflineStore;
  auctionId: string;
  /**
   * The lots whose thumbnails are owed (the record's list); `'all'` checks every lot of the auction
   * (a record synced before BL-2429, which has never had thumbnails).
   */
  lotIds: Iterable<string> | 'all';
  /** True once the pass should give way: checked between batches, and during a 429's wait. */
  shouldStop: () => boolean;
  /** Called with the count once the pass knows it, and after every batch. */
  onProgress?: (progress: ThumbnailProgress) => void;
  /** Awaited after every batch that stored a thumbnail, to keep the auction's aggregate (BL-2609). */
  onBatchStored?: (progress: ThumbnailProgress) => Promise<void>;
  /** The clock a 429's wait runs on; a timer in production. */
  sleep?: (ms: number) => Promise<void>;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * One auction's thumbnail pass (BUG-2124): match its thumbnails to its stored lots, then download
 * what the owed lots still lack, a batch at a time. Thumbnails of lots no longer under the auction
 * are removed, and an owed lot's removed media lose theirs. A thumbnail already on the device is
 * never asked for again, so a pass that stops (the app left the foreground, went offline, or
 * another sync is waiting) resumes where it stopped.
 *
 * @param input - the store, the auction, the owed lots and the stop check.
 * @returns what is still owed, and what failed.
 * @sideEffect Downloads thumbnails and writes and deletes them in the device store.
 */
export async function syncAuctionThumbnails(input: AuctionThumbnailPassInput): Promise<AuctionThumbnailOutcome> {
  const { store, auctionId } = input;
  const inAuction = new Set(
    (await store.records.entriesByParent({ collection: 'lots', parentKey: auctionId })).map((entry) => entry.key),
  );
  const heldEntries = await store.thumbnails.entriesByAuction(auctionId);
  const held = new Set(heldEntries.map((entry) => entry.key));
  const owed = input.lotIds === 'all' ? [...inAuction] : [...new Set(input.lotIds)].filter((lotId) => inAuction.has(lotId));
  const expected = await expectedSources(store, owed);
  const expectedKeys = new Set(expected.map((source) => source.key));
  const owedSet = new Set(owed);

  const stale = [...held].filter((key) => {
    const lotId = lotIdOfKey(key);
    return !inAuction.has(lotId) || (owedSet.has(lotId) && !expectedKeys.has(key));
  });
  if (stale.length > 0) await store.thumbnails.deleteMany({ auctionId, keys: stale });

  const missing = expected.filter((source) => !held.has(source.key));
  const staleSet = new Set(stale);
  const kept = heldEntries.filter((entry) => !staleSet.has(entry.key));
  return downloadThumbnails({ ...input, missing, alreadySaved: kept.length, alreadySavedBytes: kept.reduce((sum, entry) => sum + entry.bytes, 0) });
}

/**
 * Remove the thumbnails of lots no longer under the auction (deleted, departed, pruned, moved),
 * from the index alone. The lots step runs it when a page removed lots, so a removal that owes no
 * photo pass still frees its thumbnails (BUG-2124).
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction.
 * @returns how many thumbnails were removed.
 * @sideEffect Deletes thumbnails from the device store.
 */
export async function removeDepartedLotThumbnails(input: { store: OfflineStore; auctionId: string }): Promise<number> {
  const { store, auctionId } = input;
  const held = await store.thumbnails.entriesByAuction(auctionId);
  if (held.length === 0) return 0;
  const inAuction = new Set(
    (await store.records.entriesByParent({ collection: 'lots', parentKey: auctionId })).map((entry) => entry.key),
  );
  const departed = held.map((entry) => entry.key).filter((key) => !inAuction.has(lotIdOfKey(key)));
  return departed.length === 0 ? 0 : store.thumbnails.deleteMany({ auctionId, keys: departed });
}

/**
 * @param store - the scope's device store.
 * @param lotIds - the lots to read.
 * @returns their thumbnail sources, read a batch of lots at a time so only the sources are held.
 */
async function expectedSources(store: OfflineStore, lotIds: string[]): Promise<OfflineThumbnailSource[]> {
  const sources: OfflineThumbnailSource[] = [];
  for (let i = 0; i < lotIds.length; i += LOT_READ_BATCH) {
    const lots = await store.records.getMany({ collection: 'lots', keys: lotIds.slice(i, i + LOT_READ_BATCH) });
    for (const lot of lots) if (lot != null) sources.push(...thumbnailSources(lot));
  }
  return sources;
}

/** A missing thumbnail and how many times the edge has throttled it. */
interface QueuedThumbnail {
  source: OfflineThumbnailSource;
  throttled: number;
}

/** What one batch's answers came to. */
interface BatchTally {
  stored: number;
  bytes: number;
  /** Throttled ones to ask for again after the wait. */
  again: QueuedThumbnail[];
}

/** Waits out a 429's window, returning early once the pass should stop. */
async function waitOutThrottle(shouldStop: () => boolean, sleep: (ms: number) => Promise<void>): Promise<void> {
  for (let waited = 0; waited < RATE_LIMIT_WAIT_MS && !shouldStop(); waited += WAIT_STEP_MS) await sleep(WAIT_STEP_MS);
}

/** Download and store the missing thumbnails in batches through the store, stopping between batches when asked. */
async function downloadThumbnails(
  input: AuctionThumbnailPassInput & { missing: OfflineThumbnailSource[]; alreadySaved: number; alreadySavedBytes: number },
): Promise<AuctionThumbnailOutcome> {
  const { store, auctionId, shouldStop, onProgress } = input;
  const sleep = input.sleep ?? realSleep;
  const queue: QueuedThumbnail[] = input.missing.map((source) => ({ source, throttled: 0 }));
  const failedLotIds = new Set<string>();
  const outcome = { failed: 0, firstError: null as Error | null, bytesStored: 0, saved: input.alreadySaved };
  const total = input.alreadySaved + queue.length;
  const progress = (): ThumbnailProgress => ({
    saved: outcome.saved,
    total,
    bytesStored: outcome.bytesStored,
    thumbnails: { count: outcome.saved, bytes: input.alreadySavedBytes + outcome.bytesStored },
  });
  const report = () => onProgress?.(progress());
  if (queue.length > 0) report();
  let next = 0;
  let stopped = false;
  while (next < queue.length) {
    if (shouldStop()) {
      stopped = true;
      break;
    }
    const batch = queue.slice(next, next + FETCH_BATCH);
    next += batch.length;
    const results = await store.thumbnails.download({
      items: batch.map((item) => ({ key: item.source.key, auctionId, url: thumbnailUrl(item.source.src) })),
      concurrency: FETCH_CONCURRENCY,
    });
    const tally: BatchTally = { stored: 0, bytes: 0, again: [] };
    results.forEach((result, i) => {
      const item = batch[i];
      if (result.stored) {
        tally.stored += 1;
        tally.bytes += result.bytes;
        return;
      }
      const kind = classifyThumbnailFailure(result.status);
      if (kind === 'throttled' && item.throttled + 1 < RATE_LIMITED_ATTEMPTS) {
        tally.again.push({ source: item.source, throttled: item.throttled + 1 });
        return;
      }
      // Counted and reported by the caller as a failed sync, with this error as its cause.
      outcome.failed += 1;
      outcome.firstError ??= failureError(result);
      // A refusal will not change on its own: report it once, and retry only a transient failure.
      if (kind !== 'refused') failedLotIds.add(item.source.lotId);
    });
    outcome.saved += tally.stored;
    outcome.bytesStored += tally.bytes;
    report();
    if (tally.stored > 0) await input.onBatchStored?.(progress());
    if (tally.again.length > 0) {
      queue.push(...tally.again);
      await waitOutThrottle(shouldStop, sleep);
    }
  }
  // A stopped pass still owes every lot it did not reach; the next pass starts there.
  for (const item of queue.slice(next)) failedLotIds.add(item.source.lotId);
  return { owedLotIds: [...failedLotIds], stopped, failed: outcome.failed, firstError: outcome.firstError, bytesStored: outcome.bytesStored };
}
